<template>
  <!-- ── ONE till (its own zone page). A barlist of a single row is not a
       comparison, so the store-level shape is wrong here. The question a single
       till's page answers is WHEN it works — the headline rate plus the profile. -->
  <ChartGrid v-if="single" class="ctx-solo-grid">
    <!-- WHEN in the day. BARS, not a line: these are discrete counts per hour bucket,
         and a line would imply you could read a value between two hours. -->
    <ChartPanel
      :title="$t('service.checkout.soloTitle')"
      icon="bi-basket"
      :height="300"
      :accent="accent"
      :empty="!measured.length"
      :emptyText="$t('service.checkout.soloEmpty')"
    >
      <div v-if="measured.length" class="ctx-solo">
        <div class="ctx-kpi">
          <span class="ctx-kpi-num" :style="{ color: accent }">{{ single.perHour }}</span>
          <span class="ctx-kpi-unit">{{ $t('service.checkout.perHourAvg') }}</span>
          <span class="ctx-kpi-sub">
            {{ $t('service.checkout.overSpan', { n: fmtInt(single.total), span: timeSpan.toLowerCase() }) }}
          </span>
        </div>
        <VChart
          class="ctx-chart ctx-chart--solo"
          :option="soloHourlyOption"
          :notMerge="true"
          autoresize
        />
      </div>
    </ChartPanel>

    <!-- HOW LONG each one takes. The daily trend that was here has been removed: it
         was the same shape as the zone's existing entries chart, only scaled — real
         transactions are a fairly steady share of entries day to day, so it added a
         panel and no information.
         This does not exist anywhere else on the page, and it is the other half of
         throughput: a till doing fewer per hour because each takes longer is a
         different problem from one that is simply quiet. A LINE, matching the app's
         own ChartLineAverageDurationDetectionZone — average duration is drawn as a line
         everywhere else here. -->
    <ChartPanel
      :title="$t('service.checkout.timeTitle')"
      icon="bi-stopwatch"
      :height="300"
      :accent="accent"
      :empty="!hasDurations"
      :emptyText="$t('service.checkout.timeEmpty')"
    >
      <VChart
        class="ctx-chart"
        :option="durationOption"
        :notMerge="true"
        autoresize
        style="width: 100%; height: 100%;"
      />
    </ChartPanel>
  </ChartGrid>

  <!-- ── ALL tills (store level). Two questions, two panels. -->
  <ChartGrid v-else-if="zones.length" class="ctx-grid">
    <!-- WHICH till gets people through. A barlist, not a chart: it is the idiom this
         page already uses for comparing zones of one type ("Visitors by Product Zone"),
         the zone NAME is the thing being read, and it sidesteps needing a distinct
         colour per till when every till shares one zone-type colour. -->
    <ChartPanel
      :title="$t('service.checkout.perHourTitle')"
      icon="bi-bar-chart-steps"
      height="auto"
      :accent="accent"
    >
      <ul class="ctx-bars">
        <li v-for="z in ranked" :key="z.zone" :class="{ 'is-nodata': z.noData }">
          <span class="ctx-bar-name">{{ z.zone }}</span>
          <template v-if="z.noData">
            <!-- A till that exists but has no segmented data yet. Shown rather than
                 dropped: silently omitting it makes the store total look like the whole
                 story when one lane is missing from it. -->
            <span class="ctx-nodata">{{ $t('service.checkout.notMeasured') }}</span>
          </template>
          <template v-else>
            <span class="ctx-bar-track">
              <span class="ctx-bar-fill"
                    :style="{ width: barWidth(z.perHour), background: accent }"></span>
            </span>
            <!-- Just the rate. The panel title already says what is being counted, so
                 repeating "transactions" here is noise.
                 The share-of-visits figure that used to sit beside this was removed: the
                 27 is ALREADY the real count, so what it was drawn from does not qualify
                 it. That share measures how much through-traffic crosses the till's
                 polygon — a zone-geometry diagnostic, not something a manager acts on
                 from a throughput comparison. -->
            <span class="ctx-bar-val">{{ z.perHour }}<small>{{ $t('service.checkout.perHr') }}</small></span>
          </template>
        </li>
      </ul>
    </ChartPanel>

    <!-- WHEN each till is actually working. Lines rather than grouped bars: hour-of-day
         is a continuous axis, and a bar per till per hour would be dozens to read. -->
    <ChartPanel
      :title="$t('service.checkout.byHourTitle')"
      icon="bi-clock-history"
      :height="320"
      :accent="accent"
      :empty="!measured.length"
    >
      <VChart
        class="ctx-chart"
        :option="hourlyOption"
        :notMerge="true"
        autoresize
        style="width: 100%; height: 100%;"
      />
    </ChartPanel>
  </ChartGrid>
</template>

<script>
/**
 * Checkout throughput — how many REAL transactions each till gets through.
 *
 * "Real" is the pipeline's existing definition, not a new one: a visit to the
 * interaction zone whose duration exceeds that zone's own short-lingerer cutoff. The
 * comment in bgtaskdatafetching puts it best — you cannot scan, pay and bag in a few
 * seconds, so a brief presence at a till is somebody walking past.
 *
 * Presentation only. The demo store feeds it a static JSON block; production will feed
 * it the same shape from the nightly run.
 *
 * Two panels answering two different questions — WHICH till gets people through, and
 * WHEN each one is working. Neither answers the other.
 */
import { defineComponent, computed } from 'vue'
import VChart from 'vue-echarts'
import { use } from 'echarts/core'
import { BarChart, LineChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'

import ChartGrid from '@/components/ui/ChartGrid.vue'
import ChartPanel from '@/components/ui/ChartPanel.vue'
import { detection_zone_palettes } from '@/constants.js'
import { useI18n } from 'vue-i18n'
import { currentLanguage } from '@/i18n'
import { formatNumber, formatDuration } from '@/i18n/format'

use([BarChart, LineChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer])

export default defineComponent({
  name: 'CheckoutTransactionsPanel',
  components: { VChart, ChartGrid, ChartPanel },
  props: {
    // { byZone: { name: { hourlyRealTransactions, realTransactionsPerHour,
    //                     realTransactions, visitors, noData? } }, openHour, closeHour }
    data: { type: Object, default: null },
    // Restrict to one zone (the zone detail page); omit for the store-level compare.
    zoneName: { type: String, default: null },
    // Only rendered in the single-till layout, where the total needs a period beside it.
    timeSpan: { type: String, default: 'Last 30 days' },
    accent: { type: String, default: 'rgb(211, 167, 65)' },
  },
  setup(props) {
    const { t } = useI18n()
    const isEn = () => currentLanguage() === 'en'
    const zones = computed(() => {
      const by = props.data?.byZone || {}
      const names = props.zoneName ? [props.zoneName].filter(n => by[n]) : Object.keys(by)
      return names.map(n => ({
        zone: n,
        noData: !!by[n].noData,
        total: by[n].realTransactions || 0,
        secs: by[n].hourlyAvgTransactionSec || {},
        // A transaction is a whole customer. 26.9 of one is not a thing.
        perHour: Math.round(by[n].realTransactionsPerHour || 0),
        hourly: by[n].hourlyRealTransactions || {},
      }))
    })

    // Measured tills first, by throughput; unmeasured ones last rather than ranked at
    // zero, which would read as "this till served nobody".
    const ranked = computed(() => [...zones.value].sort(
      (a, b) => (a.noData - b.noData) || (b.perHour - a.perHour)))

    const measured = computed(() => zones.value.filter(z => !z.noData && Object.keys(z.hourly).length))

    // Set only on a single till's own page. Drives the compact layout: one row of a
    // barlist is not a comparison, so that shape belongs to the store view only.
    const single = computed(() =>
      props.zoneName ? (zones.value.find(z => z.zone === props.zoneName) || null) : null)

    const fmtInt = (n) => (isEn() ? Number(n || 0).toLocaleString() : formatNumber(Number(n || 0)))
    const fmtDur = (s) => {
      if (!isEn()) return formatDuration(s || 0)
      s = Math.round(s || 0)
      return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`.replace(' 0s', 'm')
    }

    const hasDurations = computed(() =>
      Object.values(single.value?.secs || {}).some(v => v > 0))

    const maxPerHour = computed(() => Math.max(1, ...zones.value.map(z => z.perHour || 0)))
    // Proportional to the busiest till, floored so a quiet lane still shows a mark.
    const barWidth = (v) => `${Math.max(4, ((v || 0) / maxPerHour.value) * 100)}%`

    // The union of hours any measured till was open, so the two tills share one axis
    // rather than each drawing its own.
    const hours = computed(() => {
      const all = new Set()
      measured.value.forEach(z => Object.keys(z.hourly).forEach(h => all.add(h)))
      return [...all].sort()
    })

    // Shared axis furniture — the two solo charts differ only in mark type.
    const _axis = {
      axisTick: { show: false },
      axisLine: { lineStyle: { color: '#e5e7eb' } },
      axisLabel: { fontSize: 10, color: '#6b7280' },
    }

    const soloHourlyOption = computed(() => ({
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' },
                 valueFormatter: (v) => t('service.checkout.transactions', { n: v }) },
      grid: { top: 14, right: 12, bottom: 26, left: 38 },
      xAxis: { type: 'category', data: hours.value.map(h => `${h}:00`), ..._axis,
               splitLine: { show: false } },
      yAxis: { type: 'value', minInterval: 1,
               axisLabel: { fontSize: 10, color: '#6b7280' },
               splitLine: { lineStyle: { color: '#f1f3f5' } } },
      series: [{
        type: 'bar',
        data: hours.value.map(h => Math.round(single.value?.hourly?.[h] ?? 0)),
        // Square tops: no other bar chart in the app rounds its bars.
        itemStyle: { color: props.accent },
        barMaxWidth: 26,
      }],
    }))

    const durationOption = computed(() => ({
      tooltip: { trigger: 'axis', valueFormatter: (v) => fmtDur(v) },
      grid: { top: 14, right: 12, bottom: 26, left: 46 },
      xAxis: { type: 'category', data: hours.value.map(h => `${h}:00`), ..._axis,
               splitLine: { show: false } },
      yAxis: { type: 'value',
               axisLabel: { fontSize: 10, color: '#6b7280', formatter: (v) => fmtDur(v) },
               splitLine: { lineStyle: { color: '#f1f3f5' } } },
      series: [{
        type: 'line', smooth: true, symbol: 'circle', symbolSize: 5,
        data: hours.value.map(h => Math.round(single.value?.secs?.[h] ?? 0)),
        lineStyle: { width: 2 },
        itemStyle: { color: props.accent },
      }],
    }))

    // Every till shares ONE zone-type colour, so the categorical palette cannot separate
    // them. The interaction zone's own heatmap ramp is four steps of that same hue —
    // built as a family, so the till series stay recognisably "checkout" rather than
    // borrowing another zone type's identity.
    const palette = computed(() => {
      const p = detection_zone_palettes['Checkout Interaction Zone']
      return (p && p.heatmap ? [...p.heatmap].reverse() : [])
        .map(c => c.replace(/rgba\(([^)]+),\s*[\d.]+\)/, 'rgb($1)'))
    })

    const showLegend = computed(() => measured.value.length > 1)

    const hourlyOption = computed(() => ({
      tooltip: { trigger: 'axis', valueFormatter: (v) => t('service.checkout.transactions', { n: v }) },
      legend: showLegend.value
        ? { top: 0, textStyle: { fontSize: 10, color: '#6b7280' }, itemHeight: 8, itemGap: 14 }
        : { show: false },
      // The plot has to start BELOW the legend, or the busiest till's peak draws into it.
      grid: { top: showLegend.value ? 56 : 16, right: 14, bottom: 28, left: 42 },
      xAxis: { type: 'category', data: hours.value.map(h => `${h}:00`), ..._axis },
      yAxis: {
        type: 'value', name: t('service.checkout.perHourAxis'), minInterval: 1,
        nameTextStyle: { fontSize: 10, color: '#9aa1a9', align: 'left' },
        axisLabel: { fontSize: 10, color: '#6b7280' },
        splitLine: { lineStyle: { color: '#f1f3f5' } },
      },
      series: [
        ...measured.value.map((z, i) => ({
          name: z.zone, type: 'line', smooth: true, symbol: 'circle', symbolSize: 6,
          data: hours.value.map(h => Math.round(z.hourly[h] ?? 0)),
          lineStyle: { width: 2 },
          itemStyle: { color: palette.value[i % palette.value.length] || props.accent },
        })),
        // All tills together — what the STORE gets through in that hour. Dashed and in
        // ink rather than a further till colour, so it reads as a summary of the lines
        // below it and not as another lane.
        ...(measured.value.length > 1 ? [{
          name: t('service.checkout.allCheckouts'), type: 'line', smooth: true, symbol: 'none',
          data: hours.value.map(h => measured.value.reduce(
            (n, z) => n + Math.round(z.hourly[h] ?? 0), 0)),
          lineStyle: { width: 2, type: 'dashed' },
          itemStyle: { color: '#6b7280' },
        }] : []),
      ],
    }))

    return { zones, ranked, hours, measured, single, hasDurations,
             hourlyOption, soloHourlyOption, durationOption, barWidth, fmtInt }
  },
})
</script>

<style scoped>
.ctx-grid { --chart-min: 420px; margin-top: 18px; }
.ctx-root { margin-top: 18px; }
/* Half-width each on a wide screen, stacked below — the single full-width chart read
   as much heavier than everything else on the page. */
.ctx-solo-grid { --chart-min: 360px; margin-top: 18px; }

.ctx-solo { display: flex; flex-direction: column; height: 100%; }
.ctx-kpi { display: flex; align-items: baseline; gap: 6px; flex-wrap: wrap; }
.ctx-kpi-num { font-size: 2rem; font-weight: 700; line-height: 1; }
.ctx-kpi-unit { font-size: 0.95rem; color: #6b7280; }
.ctx-kpi-sub { font-size: 0.8rem; color: #9aa1a9; margin-left: 8px; }
.ctx-chart--solo { flex: 1 1 auto; min-height: 0; }

.ctx-bars { list-style: none; padding: 0; margin: 0; }
.ctx-bars li {
  display: grid;
  grid-template-columns: minmax(0, clamp(6rem, 34%, 12rem)) 1fr 4rem;
  align-items: center; gap: 10px; margin-bottom: 9px;
}
.ctx-bar-name {
  font-size: 0.86rem; color: #212529;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
/* Matches the page's own .barlist — 10px on #eef3f7, .85 fill. */
.ctx-bar-track { background: #eef3f7; border-radius: 999px; height: 10px; }
.ctx-bar-fill { display: block; height: 10px; border-radius: 999px; opacity: .85; }
.ctx-bar-val {
  font-size: 0.9rem; font-weight: 600; color: #212529;
  text-align: right; font-variant-numeric: tabular-nums;
}
.ctx-bar-val small { font-size: 0.7rem; font-weight: 400; color: #9aa1a9; margin-left: 1px; }
.ctx-bars li.is-nodata .ctx-bar-name { color: #9aa1a9; }
.ctx-nodata { grid-column: 2 / -1; font-size: 0.78rem; color: #9aa1a9; }

.ctx-chart { width: 100%; height: 100%; }
</style>
